'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Lock, 
  MapPin, 
  Building2,
  Eye,
  Shield,
  Star,
  ArrowRight,
  Users,
  CheckCircle,
  Home,
  Loader2
} from 'lucide-react';

/**
 * ListingsPaywall - Client Component that blocks access to listings page
 * Following 2025 Next.js 15.3.3 standards with CSS-first animations
 * Optimized for mobile-first design and Android GPU performance
 * Now includes loading states for all buttons
 */
export default function ListingsPaywall() {
  const router = useRouter();
  const [loadingStates, setLoadingStates] = useState({
    browse: false,
    claim: false,
    learnMore: false
  });

  const handleNavigation = async (path: string, buttonKey: keyof typeof loadingStates) => {
    setLoadingStates(prev => ({ ...prev, [buttonKey]: true }));
    
    // Simulate loading delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    router.push(path);
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Background Pattern - CSS-only for performance */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiM5Q0EzQUYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 md:py-16 max-w-4xl">
        <div className="text-center mb-8 md:mb-12">
          {/* Animated Lock Icon - CSS only */}
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6 paywall-bounce">
            <Lock className="h-8 w-8 md:h-10 md:w-10 text-white" />
          </div>
          
          {/* Main Heading */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 paywall-fade-in">
            🔒 Rental & Sale Listings Are Currently Restricted
          </h1>
          
          <div className="max-w-2xl mx-auto space-y-3 text-lg md:text-xl text-gray-600 paywall-fade-in-delayed">
            <p>
              We're verifying property data from landlords and property managers.
            </p>
            <p>
              Rental and sale listings will be available once enough properties are claimed and reviewed.
            </p>
          </div>
        </div>

        {/* Main CTA Card */}
        <Card className="mb-8 md:mb-12 border-2 border-blue-200 shadow-2xl backdrop-blur-sm paywall-slide-up bg-white/95">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Eye className="h-6 w-6 text-blue-600" />
              <CardTitle className="text-xl md:text-2xl text-gray-900">
                Explore verified property reviews instead
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-4">
              {/* Single line features */}
              <div className="flex items-center justify-center gap-6 text-sm md:text-base text-gray-600 font-medium">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span>Verified Reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-blue-600" />
                  <span>Honest Ratings</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-purple-600" />
                  <span>Location Insights</span>
                </div>
              </div>

              {/* Primary CTA with Loading State */}
              <Button 
                onClick={() => handleNavigation('/browse', 'browse')}
                disabled={loadingStates.browse}
                size="lg" 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold py-3 md:py-4 text-base md:text-lg transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100"
              >
                {loadingStates.browse ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    Browse Property Reviews
                    <ArrowRight className="h-5 w-5" />
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Separator className="my-8 md:my-12" />

        {/* Landlord CTA Section - Condensed */}
        <Card className="border-2 border-orange-200 shadow-xl paywall-slide-up-delayed bg-gradient-to-br from-orange-50 to-amber-50">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="h-6 w-6 text-orange-600" />
              <CardTitle className="text-xl md:text-2xl text-gray-900">
                Landlord or property manager?
              </CardTitle>
            </div>
            <CardDescription className="text-base md:text-lg text-gray-600">
              Claim your property to help unlock listings and respond to reviews
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Condensed benefits in single line */}
              <div className="flex items-center justify-center gap-6 text-sm md:text-base text-gray-600 font-medium">
                <div className="flex items-center gap-2">
                  <Home className="h-4 w-4 text-green-600" />
                  <span>Unlock Listings</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-blue-600" />
                  <span>Respond to Reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <span>Build Trust</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => handleNavigation('/claim', 'claim')}
                  disabled={loadingStates.claim}
                  variant="outline" 
                  size="lg" 
                  className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-100 disabled:opacity-70 disabled:cursor-not-allowed font-semibold py-3 text-base transition-all duration-300"
                >
                  {loadingStates.claim ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Claim Property
                    </div>
                  )}
                </Button>
                <Button 
                  onClick={() => handleNavigation('/for-landlords', 'learnMore')}
                  disabled={loadingStates.learnMore}
                  variant="outline" 
                  size="lg" 
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-70 disabled:cursor-not-allowed font-semibold py-3 text-base transition-all duration-300"
                >
                  {loadingStates.learnMore ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Users className="h-5 w-5" />
                      Learn More
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Stats - Condensed */}
        <div className="mt-8 text-center paywall-fade-in-final">
          <div className="flex items-center justify-center gap-8 text-sm md:text-base text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-blue-600">500+</span>
              <span>Reviews</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-green-600">50+</span>
              <span>Cities</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-purple-600">95%</span>
              <span>Satisfaction</span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations - Optimized for mobile/Android */}
      <style jsx>{`
        @keyframes paywall-bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0) scale(1);
          }
          40% {
            transform: translateY(-8px) scale(1.05);
          }
          60% {
            transform: translateY(-4px) scale(1.02);
          }
        }

        @keyframes paywall-fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes paywall-slide-up {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Animation classes with CSS-only timing */
        .paywall-bounce {
          animation: paywall-bounce 2s ease-in-out infinite;
        }

        .paywall-fade-in {
          animation: paywall-fade-in 0.8s ease-out forwards;
        }

        .paywall-fade-in-delayed {
          animation: paywall-fade-in 0.8s ease-out 0.2s both;
        }

        .paywall-slide-up {
          animation: paywall-slide-up 0.8s ease-out 0.4s both;
        }

        .paywall-slide-up-delayed {
          animation: paywall-slide-up 0.8s ease-out 0.6s both;
        }

        .paywall-fade-in-final {
          animation: paywall-fade-in 0.8s ease-out 0.8s both;
        }

        /* Performance optimizations for Android */
        .paywall-bounce,
        .paywall-fade-in,
        .paywall-fade-in-delayed,
        .paywall-slide-up,
        .paywall-slide-up-delayed,
        .paywall-fade-in-final {
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform: translateZ(0);
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .paywall-bounce {
            animation: none;
          }
          
          .paywall-fade-in,
          .paywall-fade-in-delayed,
          .paywall-slide-up,
          .paywall-slide-up-delayed,
          .paywall-fade-in-final {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
} 